import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';

import { AppListResponse } from '../models/app-list/app-list-response';
import { AppListResult } from '../models/app-list/app-list-result';
import { SESSION_INFO_COMMAND, SessionInfoResponse, SessionInfoResult } from '../models/session/info/SessionInfo';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  public session: SessionInfo;

  private apps: AppListResult[];

  get session$(): Observable<SessionInfo> {
    return this.apiService.sessionApi.pipe(
      map((res) => new SessionInfo(res?.cmd?.params)),
      tap((res) => {
        this.session = res;
      }),
      catchError(() => {
        return of(new SessionInfo(undefined));
      })
    );
  }

  get apps$() {
    if (this.apps) {
      return of(this.apps);
    }

    return this.session$.pipe(
      filter((session) => !!session),
      switchMap(() => this.apiService.get<AppListResponse>('portal_app.list')),
      map((res) => res?.cmd?.params?.result),
      tap((apps) => (this.apps = apps))
    );
  }

  constructor(private apiService: ApiService) {}
}

export class SessionInfo {
  get hasSuperAdmin() {
    return this.data?.hasSuperAdmin;
  }
  get hasSuperOps() {
    return this.data?.hasSuperOps;
  }
  get hasOrgAdmin() {
    return this.data?.hasOrgAdmin;
  }

  get euiccEnabled() {
    return this.data?.orgProfileSettings?.enEuicc;
  }

  get orgId(): string {
    return this.data?.orgId;
  }

  get orgKey(): string {
    return this.data?.orgKey;
  }

  get isValid(): boolean {
    return !!this.data;
  }

  constructor(private data: SessionInfoResult | undefined) {}

  //TODO: check if this is the correct way to check permissions
  // looks like something is missing here
  hasPermission(permission: string): boolean {
    // Auto allow for super admins
    if (this.data?.hasSuperAdmin) {
      return true;
    }

    permission = permission.toLowerCase();

    // Only superadmins have access to superadmin features
    if (permission == 'superadmin') {
      return this.hasSuperAdmin;
    }

    // Superadmins and superops have access to superops features
    if (permission == 'superops') {
      return this.hasSuperAdmin || this.hasSuperOps;
    }

    // Orgadmin, superadmins and superopscan have access to orgadmin features
    if (permission == 'orgadmin') {
      return this.hasOrgAdmin || this.hasSuperAdmin || this.hasSuperOps;
    }

    // Allow OrgAdmin, SuperOps, and SuperAdmin to execute all other actions/services
    if (this.hasOrgAdmin || this.hasSuperOps || this.hasSuperAdmin) {
      return true;
    }

    if (!permission.includes('.')) {
      return false;
    }
  }
}
